import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import CustomSelect from "../DesignSystem/Dropdowns/CustomSelect";
import InputField from "../DesignSystem/Inputs/InputField";
import Checkbox from "../DesignSystem/Inputs/Checkbox";
import PrimaryButton from "../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../DesignSystem/Buttons/SecondaryButton";
import AlertBanner from "../DesignSystem/AlertBanner/AlertBanner";
import { copyFirestoreCollection } from "./services/firestoreCopyApi";
import "../../css/Administrator/FirestoreCopyPage.css";

const DATABASE_OPTIONS = Object.freeze([
  Object.freeze({ id: "(default)", name: "Default database (read only)" }),
  Object.freeze({ id: "rosemount-dev", name: "rosemount-dev" }),
]);

export default function FirestoreCopyPage() {
  const [fromDatabase, setFromDatabase] = useState("(default)");
  const [collectionName, setCollectionName] = useState("Advisers");
  const [confirmed, setConfirmed] = useState(false);
  const [copying, setCopying] = useState(false);
  const [banner, setBanner] = useState(null);

  const runCopy = async (dryRun) => {
    if (!collectionName.trim()) {
      setBanner({ type: "error", message: "Enter a collection name." });
      return;
    }
    if (!dryRun && !confirmed) {
      setBanner({
        type: "error",
        message: "Confirm the destination before copying the collection.",
      });
      return;
    }

    setCopying(true);
    setBanner(null);
    try {
      const result = await copyFirestoreCollection({
        fromDatabase,
        toDatabase: "rosemount-dev",
        collectionName: collectionName.trim(),
        dryRun,
      });
      setBanner({
        type: "success",
        message: dryRun
          ? `Check complete: ${result.documentCount} documents across ${result.collectionCount} collections would be copied.`
          : `Copy complete: ${result.documentCount} documents across ${result.collectionCount} collections were written to rosemount-dev.`,
      });
      if (!dryRun) setConfirmed(false);
    } catch (error) {
      setBanner({
        type: "error",
        message: error?.message || "The collection could not be copied.",
      });
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Firestore Collection Copy</title>
      </Helmet>
      <Sidebar />

      <main className="Main-Container firestore-copy-page">
        <Breadcrumb
          items={[
            { label: "Administrator", to: "/Department/Administrator" },
            { label: "Firestore Collection Copy" },
          ]}
        />

        <section className="firestore-copy-card">
          <div className="firestore-copy-heading">
            <h2>Copy Firestore Collection</h2>
            <p className="Content">
              Reads from the selected source and writes only to rosemount-dev.
              Existing destination documents with matching paths are updated;
              destination-only documents are not deleted.
            </p>
          </div>

          {banner && (
            <AlertBanner
              type={banner.type}
              message={banner.message}
              onClose={() => setBanner(null)}
            />
          )}

          <div className="firestore-copy-grid">
            <CustomSelect
              label="From database"
              required
              options={DATABASE_OPTIONS}
              value={fromDatabase}
              onChange={(event) => setFromDatabase(event.target.value)}
              clearable={false}
              searchable={false}
              disabled={copying}
            />
            <CustomSelect
              label="To database"
              required
              options={DATABASE_OPTIONS}
              value="rosemount-dev"
              onChange={() => undefined}
              clearable={false}
              searchable={false}
              disabled
            />
            <InputField
              id="firestore-copy-collection"
              label="Top-level collection"
              required
              value={collectionName}
              onChange={(event) => setCollectionName(event.target.value)}
              disabled={copying}
              placeholder="For example, Advisers"
            />
          </div>

          <AlertBanner
            type="warning"
            showClose={false}
            message="The source is read only. This tool cannot write to the default database or any destination other than rosemount-dev."
          />

          <Checkbox
            id="confirm-firestore-copy"
            label={`I confirm that ${collectionName || "the selected collection"} may be written to rosemount-dev.`}
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            disabled={copying}
          />

          <div className="firestore-copy-actions">
            <SecondaryButton
              label={copying ? "Checking…" : "Check Documents"}
              onClick={() => runCopy(true)}
              disabled={copying}
            />
            <PrimaryButton
              type="button"
              label={copying ? "Copying…" : "Copy Collection"}
              onClick={() => runCopy(false)}
              disabled={copying || !confirmed}
            />
          </div>
        </section>
      </main>
    </div>
  );
}
